import API from './api';
import { generate as generateWords } from 'random-words';
import getVariantPrices from './queries/getVariantPrices';
import { getCountryCode } from './utils/currency';
/**
 * Actions
 *
 * @author John Noel <john.noel@joipolloi.com>
 * @package Minifigs
 */

export const UPDATE_PROJECT_TITLE = 'UPDATE_PROJECT_TITLE';
export const SET_PROJECT_KEY = 'SET_PROJECT_KEY';

export function updateProjectTitle(buildIndex, title) {
    return {
        type: UPDATE_PROJECT_TITLE,
        payload: {
            buildIndex,
            title,
        },
    };
}

export function setProjectKey(buildIndex, projectKey, { force = false } = {}) {
    return {
        type: 'SET_PROJECT_KEY',
        payload: { buildIndex, projectKey, force },
    };
}

// Ensure a human-readable projectKey exists (three words + short id)
export function ensureProjectKey(buildIndex) {
    return (dispatch, getState) => {
        const state = getState();
        const builds = state.builds || [];
        if (buildIndex < 0 || buildIndex >= builds.length) return;
        const build = builds[buildIndex];
        const existing = build?.projectKey;
        const goodPattern = /^[a-z]+_[a-z]+_[a-z]+_[a-z0-9]{4,8}$/;
        if (existing && goodPattern.test(existing)) return;

        let words;
        try {
            words = generateWords({ exactly: 3, join: '_' }).toLowerCase();
        } catch (e) {
            words = 'fig_project_builder';
        }
        const shortId = `${Date.now().toString(36).slice(-3)}${Math.random()
            .toString(36)
            .replace(/[^a-z0-9]/g, '')
            .slice(0, 3)}`;
        const existingKeys = builds.filter((b) => b && b.projectKey).map((b) => b.projectKey.toLowerCase());
        let attempt = 0;
        let candidate;
        do {
            candidate = `${words}_${shortId}`;
            attempt += 1;
        } while (existingKeys.includes(candidate.toLowerCase()) && attempt < 3);
        dispatch(setProjectKey(buildIndex, candidate, { force: true }));
    };
}

export const TOGGLE_NAVIGATION = 'TOGGLE_NAVIGATION';

export function toggleNavigation() {
    return {
        type: TOGGLE_NAVIGATION,
        payload: null,
    };
}

export const HIDE_TOAST = 'HIDE_TOAST';

export function hideToast() {
    return {
        type: HIDE_TOAST,
        payload: null,
    };
}

// Search open/close state (moved from Browser local state to redux)
export const SET_SEARCH_OPEN = 'SET_SEARCH_OPEN';
export function setSearchOpen(isOpen) {
    return {
        type: SET_SEARCH_OPEN,
        payload: { isOpen },
    };
}

//
// Select a section, receive top level categories for that section
//
export const SELECTING_SECTION = 'SELECTING_SECTION';
export const RECEIVED_CATEGORIES = 'RECEIVED_CATEGORIES';

export function selectSection(section) {
    return async (dispatch) => {
        dispatch({
            type: SELECTING_SECTION,
            payload: section,
        });

        const categories = await API.getCategories(section);
        dispatch(receivedCategories(categories));
    };
}

function receivedCategories(categories) {
    return {
        type: RECEIVED_CATEGORIES,
        payload: categories,
    };
}

// Fetch and save config to redux
export const SET_CONFIG = 'SET_CONFIG';
export function getConfig() {
    return async (dispatch) => {
        const config = await API.getConfig();
        dispatch({
            type: SET_CONFIG,
            payload: config,
        });
    };
}

//
// Select a section & category, receive products for that section & category
//
export const SELECTING_CATEGORY = 'SELECTING_CATEGORY';
export const RECEIVED_PRODUCTS = 'RECEIVED_PRODUCTS';
export const SET_AFTER_CURSOR = 'SET_AFTER_CURSOR';
export const SET_CURRENT_FILTERS = 'SET_CURRENT_FILTERS';
export const SET_LOADING_TRUE = 'SET_LOADING_TRUE';
export const SET_LOADING_MORE_TRUE = 'SET_LOADING_MORE_TRUE';

export function selectSectionAndCategory(category, filters, skintone) {
    // console.log('%c Fetch products, start : ', 'color: #47f953; font-size: 18px;', new Date(Date.now()));
    // console.log('selectSectionAndCategory: ', { category, filters, skintone });
    return async (dispatch) => {
        dispatch({
            type: SELECTING_SECTION,
            payload: category.id,
        });

        dispatch({
            type: SELECTING_CATEGORY,
            payload: category,
        });

        dispatch({
            type: SET_CURRENT_FILTERS,
            payload: filters,
        });

        const prods = await API.getProducts(category.id, category.handle, filters, null, skintone);
        const prodAttr = [];
        const prodAttrProcessed = [];
        const { products, hasNextPage } = prods;

        products?.forEach((product) => {
            const prodIdParsed = product.node.id.split('/').pop();
            prodAttr.push([product.node.id, API.getProductAttribute(prodIdParsed)]);
        });

        prodAttr.forEach((productAttribute) => {
            setTimeout(() => {
                productAttribute[1].then((result_1) => {
                    prodAttrProcessed.push(result_1.option_product);
                });
            }, 750);
        });

        dispatch({
            type: SET_AFTER_CURSOR,
            payload: {
                afterCursor: products ? products[products.length - 1]?.cursor : false,
                hasNextPage,
            },
        });

        dispatch(receivedProducts(products, prodAttrProcessed));
    };
}

export function loadMoreProducts(activeSection, activeHandle, afterCursor, currentProducts, filters, skintone) {
    // console.log('%c Fetch products, start load more : ', 'color: #47f953; font-size: 18px;', new Date(Date.now()));

    return async (dispatch) => {
        // Use a distinct loadingMore flag so we don't obscure existing products with the full-screen loader
        dispatch({
            type: SET_LOADING_MORE_TRUE,
        });

        const prods = await API.getProducts(activeSection, activeHandle, filters, afterCursor, skintone);
        const prodAttr = [];
        const prodAttrProcessed = [];
        const { products, hasNextPage } = prods;

        products?.forEach((product) => {
            const prodIdParsed = product.node.id.split('/').pop();
            prodAttr.push([product.node.id, API.getProductAttribute(prodIdParsed)]);
        });

        prodAttr.forEach((productAttribute) => {
            setTimeout(() => {
                productAttribute[1].then((result_1) => {
                    prodAttrProcessed.push(result_1.option_product);
                });
            }, 750);
        });

        dispatch({
            type: SET_AFTER_CURSOR,
            payload: {
                afterCursor: products ? products[products.length - 1]?.cursor : false,
                hasNextPage,
            },
        });

        if (products.length > 0) {
            dispatch(receivedProducts(products, prodAttrProcessed, currentProducts));
        }
    };
}

function receivedProducts(products, prodAttrProcessed, currentProducts = null) {
    const prods = products;
    const options = prodAttrProcessed;

    // console.log('%c Fetch products, receivedProducts... : ', 'color: blue; font-size: 16px;', {
    //     products,
    //     prodAttrProcessed,
    //     currentProducts,
    //     prods,
    //     options,
    // }, new Date(Date.now()));

    return async (dispatch) => {
        setTimeout(() => {
            for (let i = 0, n = prods?.length; i < n; i += 1) {
                prods[i].boldProductOptions = Object.entries(options)[i];
            }

            dispatch({
                type: RECEIVED_PRODUCTS,
                payload: currentProducts ? [...currentProducts, ...prods] : prods,
            });
        }, 750);

        // console.log('%c Fetch products, end... : ', 'color: #f94747; font-size: 18px;', new Date(Date.now()));
    };
}

//
// Select a product for a section
//
export const SELECTING_PRODUCT = 'SELECTING_PRODUCT';
export const RECEIVED_PRODUCT_DETAILS = 'RECEIVED_PRODUCT_DETAILS';
export const CACHE_PRODUCT_DETAILS = 'CACHE_PRODUCT_DETAILS';

// Track Customise overlay visibility
export const SET_CUSTOMISE_OVERLAY_OPEN = 'SET_CUSTOMISE_OVERLAY_OPEN';
export function setCustomiseOverlayOpen(isOpen) {
    return {
        type: SET_CUSTOMISE_OVERLAY_OPEN,
        payload: { isOpen },
    };
}

export function customiseProduct(product, opts = {} /*attributes, addons*/) {
    const { silent = false } = opts;
    return async (dispatch, getState) => {
        const prodIdParsed = product.id.split('/').pop();

        if (!silent) {
            // Only dispatch selecting state for explicit user action
            dispatch({
                type: SELECTING_PRODUCT,
                payload: product,
            });
        } else {
            // If already cached, skip network entirely
            const cached = getState()?.browser?.productDetailsCache?.[product.id];
            if (cached) return; // nothing to do; cache hit
        }

        try {
            const [productR, variations, product_attributes, terms] = await Promise.all([
                API.getProduct(prodIdParsed),
                API.getVariations(prodIdParsed, product?.handle),
                API.getProductAttribute(prodIdParsed),
                API.getAttributeTerms(prodIdParsed, prodIdParsed),
            ]);

            const fullProduct = (productR && productR.product) || product;

            if (silent) {
                return dispatch(cacheProductDetails(fullProduct, variations, product_attributes, terms));
            }
            return dispatch(receivedProductDetails(fullProduct, variations, product_attributes, terms));
        } catch (e) {
            console.error('Failed to load product customisation data', e);
            if (silent) return; // silent failures can be ignored (retry on explicit open)
            return dispatch(receivedProductDetails(product, null, null, null));
        }
    };
}

export function cacheProductDetails(product, variations, product_attribute, attributeTerms) {
    return {
        type: CACHE_PRODUCT_DETAILS,
        payload: { product, variations, product_attribute, attributeTerms },
    };
}

function receivedProductDetails(product, variations, product_attribute, attributeTerms) {
    return {
        type: RECEIVED_PRODUCT_DETAILS,
        payload: {
            product,
            variations,
            product_attribute,
            attributeTerms,
        },
    };
}

export const SELECT_PRODUCT = 'SELECT_PRODUCT';

export function selectProduct(buildIndex, product, attributes, addons, product_attribute, productFormJsonValues) {
    // attributes contains the actual variant + variant image <<<
    return {
        type: SELECT_PRODUCT,
        payload: {
            buildIndex,
            product,
            attributes,
            addons,
            product_attribute,
            productFormJsonValues,
        },
    };
}

export const SELECT_NAMED_PRODUCT = 'SELECT_NAMED_PRODUCT';

export function selectNamedProduct(
    buildIndex,
    product,
    attributes,
    addons,
    product_attribute,
    product_category,
    productFormJsonValues
) {
    // attributes contains the actual variant + variant image <<<
    return {
        type: SELECT_NAMED_PRODUCT,
        payload: {
            buildIndex,
            product,
            attributes,
            addons,
            product_attribute,
            product_category,
            productFormJsonValues,
        },
    };
}

//
// Edit a product
//
export const EDIT_PRODUCT = 'EDIT_PRODUCT';

export function editProduct(sectionId, product) {
    return {
        type: EDIT_PRODUCT,
        payload: { sectionId, product },
    };
}

//
// Remove a product
//
export const REMOVE_PRODUCT = 'REMOVE_PRODUCT';

export function removeProduct(buildIndex, sectionId) {
    return async (dispatch, getState) => {
        if (sectionId === 'bodies') {
            dispatch(
                setTorsoText(buildIndex, {
                    frontText: '',
                    backText: '',
                })
            );
            dispatch(clearTorsoImageUpload(buildIndex));
        }

        if (sectionId === 'display') {
            dispatch(clearDisplayImageUpload(buildIndex));
        }

        // Get the product being removed and the current build's projectKey
        const state = getState();
        const currentBuild = state.builds[buildIndex];
        const projectKey = currentBuild?.projectKey;

        let removedProduct = null;
        let variantIdToRemove = null;

        // Determine what's being removed based on sectionId
        if (sectionId && sectionId.startsWith('accessories')) {
            const parts = sectionId.split(':');
            const identifier = parts[1];
            const accessories = currentBuild?.selected?.accessories || [];

            if (/^\d+$/.test(identifier)) {
                // Remove by index
                const idx = parseInt(identifier, 10);
                if (idx >= 0 && idx < accessories.length) {
                    removedProduct = accessories[idx];
                }
            } else {
                // Remove by product ID
                removedProduct = accessories.find((acc) => acc?.product?.id === identifier);
            }
        } else {
            // Main section (heads, bodies, legs, etc.)
            removedProduct = currentBuild?.selected?.[sectionId];
        }

        // Extract variant ID from the removed product
        if (removedProduct?.productFormJsonValues?.id) {
            variantIdToRemove = removedProduct.productFormJsonValues.id;
        } else if (removedProduct?.productMatchAttr?.id) {
            variantIdToRemove = removedProduct.productMatchAttr.id;
        } else if (removedProduct?.attributes?.id) {
            variantIdToRemove = removedProduct.attributes.id;
        } else if (removedProduct?.product?.variants?.nodes?.[0]?.id) {
            variantIdToRemove = removedProduct.product.variants.nodes[0].id;
        } else if (removedProduct?.product?.variants?.edges?.[0]?.node?.id) {
            variantIdToRemove = removedProduct.product.variants.edges[0].node.id;
        }

        // Normalize variant ID to numeric format for cart API
        const normalizeVariantId = (rawId) => {
            if (!rawId) return rawId;
            if (typeof rawId === 'string') {
                if (rawId.startsWith('gid://')) {
                    const parts = rawId.split('/');
                    const last = parts[parts.length - 1];
                    if (/^\d+$/.test(last)) return last;
                }
                if (/^(shopify|gid):\/\//.test(rawId)) {
                    const digits = rawId.match(/(\d+)/g);
                    if (digits && digits.length) return digits[digits.length - 1];
                }
            }
            return rawId;
        };

        variantIdToRemove = normalizeVariantId(variantIdToRemove);

        // Synchronize with Shopify cart: remove the product and its Bold Options
        if (variantIdToRemove && projectKey) {
            try {
                // Fetch current cart state
                const cartResponse = await fetch('/cart.js');
                const cart = await cartResponse.json();

                // Find all cart items matching this variant ID and projectKey
                const itemsToRemove = cart.items.filter((item) => {
                    const itemVariantId = item.variant_id ? item.variant_id.toString() : item.id?.toString();
                    const matchesVariant = itemVariantId === variantIdToRemove.toString();
                    const matchesProject = item.properties?._projectKey === projectKey;
                    return matchesVariant && matchesProject;
                });

                // Also find any Bold Options "shadow variants" associated with this item
                const boldBuilderIds = itemsToRemove.map((item) => item.properties?._boldBuilderId).filter(Boolean);

                const boldShadowVariants = cart.items.filter((item) => {
                    return (
                        item.properties?._boldBuilderId &&
                        boldBuilderIds.includes(item.properties._boldBuilderId) &&
                        item.properties._boldVariantIds === undefined
                    );
                });

                // Combine all items to remove
                const allItemsToRemove = [...itemsToRemove, ...boldShadowVariants];

                // Remove each item by setting quantity to 0
                for (const item of allItemsToRemove) {
                    await fetch('/cart/change.js', {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify({
                            id: item.key || item.id,
                            quantity: 0,
                        }),
                    });
                }
            } catch (error) {
                console.error('[Builder] Failed to sync cart removal:', error);
                // Continue with Redux removal even if cart sync fails
            }
        }

        // Dispatch Redux state update
        dispatch(doRemove(buildIndex, sectionId));
    };
}

function doRemove(buildIndex, sectionId) {
    return {
        type: REMOVE_PRODUCT,
        payload: { buildIndex, sectionId },
    };
}

//
// Buy
//
export const BUYING = 'BUYING';
export const BOUGHT = 'BOUGHT';
export const ERROR_BUYING = 'ERROR_BUYING';
export const RESET_ERROR_BUYING = 'RESET_ERROR_BUYING';
export const PROJECT_ADDED_TO_CART = 'PROJECT_ADDED_TO_CART';
export const RESET_ADDED_PROJECTS = 'RESET_ADDED_PROJECTS';

export function buy(selectedBuilds) {
    return async (dispatch /*getState*/) => {
        // Validate builds before proceeding
        const validationErrors = [];
        for (let i = 0; i < selectedBuilds.length; i++) {
            const build = selectedBuilds[i];
            const selected = build.selected;
            const projectTitle = build.projectTitle || `Project ${i + 1}`;

            // Check for incomplete builds (missing critical parts)
            const hasHead = selected.heads !== null && selected.heads !== undefined;
            const hasBody = selected.bodies !== null && selected.bodies !== undefined;
            const hasLegs = selected.legs !== null && selected.legs !== undefined;

            // A complete minifig typically needs head + body + legs
            // (hats/hair, display and accessories are optional)
            if (!hasHead || !hasBody || !hasLegs) {
                validationErrors.push({
                    projectTitle,
                    message: 'This project has no minifig parts selected',
                });
            }
        }

        // If validation errors exist, show error and don't proceed
        if (validationErrors.length > 0) {
            const errorMessages = [
                'Please complete your build(s) before adding to cart:',
                ...validationErrors.map((err) => `- ${err.projectTitle}: ${err.message}`),
            ];
            dispatch({
                type: ERROR_BUYING,
                payload: {
                    errorMessage: errorMessages,
                },
            });
            return;
        }

        dispatch({
            type: BUYING,
        });

        // reset alerts
        dispatch(resetErrorOnBuy());
        dispatch(resetAddedProjects());

        const errors = [];
        try {
            for (let i = 0; i < selectedBuilds.length; i++) {
                const selectedBuild = selectedBuilds[i];
                const res = await API.buy(
                    selectedBuild.selected,
                    selectedBuild.projectTitle,
                    selectedBuild.skintone,
                    selectedBuild.projectKey,
                    selectedBuild.headFeatures
                );

                // 442: error
                if (res.status === 422) {
                    const projectTitle = selectedBuild.projectTitle;

                    // Get the product that is out of stock (There are two different error messages that can be returned here)
                    let productOutOfStock;
                    if (res.message && res.message.startsWith('The product')) {
                        productOutOfStock = res.message.split("'")[1];
                    } else if (res.message && res.message.startsWith("You can't add more")) {
                        productOutOfStock = res.message.split("You can't add more")[1].split('to the cart')[0].trim();
                    }

                    errors.push({ productOutOfStock, projectTitle });
                } else if (res.status === 'too_many_requests') {
                    dispatch({
                        type: ERROR_BUYING,
                        payload: {
                            errorMessage: [
                                'Sorry, we are currently experiencing a high volume of traffic. Please try again later.',
                            ],
                        },
                    });
                } else if (res.status === 200 || res.status === undefined) {
                    // 200: Success
                    // We store the names of the successfully added projects in case there is an error.
                    // These are displayed next to the error message
                    dispatch(projectAddedToCart(selectedBuild.projectTitle));
                    dispatch(removeSelectedBuild(selectedBuild));
                }
            }

            if (errors.length > 0) {
                let errorMessage = [];
                if (errors.length === 1) {
                    errorMessage.push(
                        `So sorry! ${errors[0].productOutOfStock
                            .split('-')[0]
                            .trim()} is now out of stock. So your project ${
                            errors[0].projectTitle
                        } cannot be added to your basket. Please go back, remove this item from your project and try again.`
                    );
                } else {
                    errorMessage.push(
                        'So sorry! The following products are now out of stock. Please go back, remove these items from your project and try again.'
                    );

                    for (let i = 0; i < errors.length; i++) {
                        errorMessage.push(
                            `- ${errors[i].productOutOfStock.split('-')[0].trim()} from project ${
                                errors[i].projectTitle
                            }`
                        );
                    }
                }

                dispatch({
                    type: ERROR_BUYING,
                    payload: {
                        errorMessage,
                    },
                });
                throw new Error(errorMessage);
            }
        } catch (error) {
            console.error(error);
            console.error('fail to add to cart');
        }

        // Only redirect if there are no errors

        dispatch(bought());
    };
}

function bought() {
    return {
        type: BOUGHT,
        payload: null,
    };
}

export function resetErrorOnBuy() {
    return {
        type: RESET_ERROR_BUYING,
        payload: null,
    };
}

//
// Start
//
export const START = 'START';

export function start() {
    return {
        type: START,
        payload: null,
    };
}

//
// Reset
//
export const RESET = 'RESET';

export function reset() {
    return {
        type: RESET,
        payload: null,
    };
}

//
// Set skin tone filter
//
export const SET_SKIN_TONE = 'SET_SKIN_TONE';

export function setSkinTone(buildIndex, skintone) {
    return {
        type: SET_SKIN_TONE,
        payload: { buildIndex, skintone },
    };
}

//
// Set active filter
//
export const SET_ACTIVE_FILTER = 'SET_ACTIVE_FILTER';

export function setActiveFilterButton(filter) {
    return {
        type: SET_ACTIVE_FILTER,
        payload: filter,
    };
}

//
// Set torso text
//
export const SET_TORSO_TEXT = 'SET_TORSO_TEXT';

export function setTorsoText(buildIndex, torsoText) {
    return {
        type: SET_TORSO_TEXT,
        payload: { buildIndex, torsoText },
    };
}

export const SET_TORSO_OVERLAY = 'SET_TORSO_OVERLAY';

export function setTorsoOverlay(buildIndex, torsoOverlays) {
    return {
        type: SET_TORSO_OVERLAY,
        payload: { buildIndex, torsoOverlays },
    };
}

export const SET_HEAD_FEATURES = 'SET_HEAD_FEATURES';

export function setHeadFeatures(buildIndex, headFeatures) {
    return {
        type: SET_HEAD_FEATURES,
        payload: { buildIndex, headFeatures },
    };
}

//
// Set torso image upload
//
export const SET_TORSO_IMAGE_UPLOAD = 'SET_TORSO_IMAGE_UPLOAD';
export const CLEAR_TORSO_IMAGE_UPLOAD = 'CLEAR_TORSO_IMAGE_UPLOAD';

export function setTorsoImageUpload(buildIndex, imageData) {
    return {
        type: SET_TORSO_IMAGE_UPLOAD,
        payload: { buildIndex, imageData },
    };
}

export function clearTorsoImageUpload(buildIndex) {
    return {
        type: CLEAR_TORSO_IMAGE_UPLOAD,
        payload: { buildIndex },
    };
}

//
// Set / clear display image upload (independent from torso)
//
export const SET_DISPLAY_IMAGE_UPLOAD = 'SET_DISPLAY_IMAGE_UPLOAD';
export const SET_DISPLAY_IMAGE_UPLOAD_DRAFT = 'SET_DISPLAY_IMAGE_UPLOAD_DRAFT';
export const CLEAR_DISPLAY_IMAGE_UPLOAD = 'CLEAR_DISPLAY_IMAGE_UPLOAD';

export function setDisplayImageUpload(buildIndex, imageData) {
    return {
        type: SET_DISPLAY_IMAGE_UPLOAD,
        payload: { buildIndex, imageData },
    };
}

export function setDisplayImageUploadDraft(buildIndex, imageData) {
    return {
        type: SET_DISPLAY_IMAGE_UPLOAD_DRAFT,
        payload: { buildIndex, imageData },
    };
}

export function clearDisplayImageUpload(buildIndex) {
    return {
        type: CLEAR_DISPLAY_IMAGE_UPLOAD,
        payload: { buildIndex },
    };
}

export const SET_CURRENT_BUILD_INDEX = 'SET_CURRENT_BUILD_INDEX';

export function setCurrentBuildIndex(buildIndex) {
    return {
        type: SET_CURRENT_BUILD_INDEX,
        payload: { buildIndex },
    };
}

export const REMOVE_BUILD = 'REMOVE_BUILD';

export function removeBuild(buildIndex) {
    return {
        type: REMOVE_BUILD,
        payload: { buildIndex },
    };
}

export const REMOVE_SELECTED_BUILD = 'REMOVE_SELECTED_BUILD';

export function removeSelectedBuild(selectedBuild) {
    return {
        type: REMOVE_SELECTED_BUILD,
        payload: { selectedBuild },
    };
}

export function projectAddedToCart(projectTitle) {
    return {
        type: PROJECT_ADDED_TO_CART,
        payload: { projectTitle },
    };
}

export function resetAddedProjects() {
    return {
        type: RESET_ADDED_PROJECTS,
        payload: null,
    };
}

export const ADD_BUILD = 'ADD_BUILD';

export function addBuild(init) {
    return {
        type: ADD_BUILD,
        payload: init,
    };
}

export const SET_SCROLL_STATE = 'SET_SCROLL_STATE';

export function setScrollState(isScrolled) {
    return {
        type: SET_SCROLL_STATE,
        payload: isScrolled,
    };
}

export const CURRENCY_CHANGED = 'CURRENCY_CHANGED';

export function currencyChanged(currencyCode) {
    return {
        type: CURRENCY_CHANGED,
        payload: currencyCode,
    };
}

export const REFRESH_SELECTED_PRICES = 'REFRESH_SELECTED_PRICES';

/**
 * Re-fetch variant prices for all selected products using the current market context.
 * Called when the buyer's country/currency changes so stored prices stay accurate.
 */
export function refreshSelectedPrices() {
    return async (dispatch, getState) => {
        const state = getState();
        const builds = state.builds || [];

        // Collect all variant GIDs from selected products across all builds
        const variantIds = new Set();
        builds.forEach((build) => {
            const selected = build.selected || {};
            Object.keys(selected).forEach((section) => {
                if (section === 'accessories' && Array.isArray(selected[section])) {
                    selected[section].forEach((acc) => {
                        if (acc?.attributes?.id) variantIds.add(acc.attributes.id);
                        if (acc?.productMatchAttr?.id) variantIds.add(acc.productMatchAttr.id);
                    });
                } else {
                    const item = selected[section];
                    if (item?.attributes?.id) variantIds.add(item.attributes.id);
                    if (item?.productMatchAttr?.id) variantIds.add(item.productMatchAttr.id);
                }
            });
        });

        if (variantIds.size === 0) return;

        const country = getCountryCode();
        const query = getVariantPrices([...variantIds], country);

        try {
            const response = await fetch('/api/2025-04/graphql.json', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/graphql',
                    'X-Shopify-Storefront-Access-Token': 'e14901ef020f42eff2b10fbed13e6e18',
                },
                body: query,
            });
            const result = await response.json();
            const nodes = result?.data?.nodes || [];

            // Build a map of variantId -> price
            const priceMap = {};
            nodes.forEach((node) => {
                if (node?.id && node?.price) {
                    priceMap[node.id] = node.price;
                }
            });

            if (Object.keys(priceMap).length > 0) {
                dispatch({
                    type: REFRESH_SELECTED_PRICES,
                    payload: priceMap,
                });
            }
        } catch (e) {
            console.error('Failed to refresh selected prices', e);
        }
    };
}
